import { useCallback, useMemo, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";

/**
 * Custom hook to store and restore table state (filters, search queries, pagination, and sorting)
 * in the URL query string and sessionStorage across route transitions.
 *
 * When users navigate from a table row to a detail page and then navigate back,
 * all filters, pagination, and search terms are 100% restored.
 * When users select a different menu in the sidebar navigation, the state is reset fresh.
 */
export function useTableState<TFilters extends Record<string, any>>(
  defaultFilters: TFilters,
  options: {
    defaultPage?: number;
    defaultPageSize?: number;
    defaultSortBy?: string;
    defaultSortOrder?: "asc" | "desc";
    storageKey?: string;
  } = {}
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const {
    defaultPage = 1,
    defaultPageSize = 100,
    defaultSortBy = "created_at",
    defaultSortOrder = "desc",
    storageKey = `mtrcb_table_state_${location.pathname}`,
  } = options;

  // On mount: If URL has no search params, restore from sessionStorage if available
  useEffect(() => {
    if (!location.search || location.search === "") {
      const saved = sessionStorage.getItem(storageKey);
      if (saved && saved.length > 1) {
        setSearchParams(new URLSearchParams(saved), { replace: true });
      }
    } else {
      sessionStorage.setItem(storageKey, location.search);
    }
  }, [location.pathname, location.search, storageKey, setSearchParams]);

  // Whenever URL search changes with active queries, keep sessionStorage in sync
  useEffect(() => {
    if (location.search && location.search !== "") {
      sessionStorage.setItem(storageKey, location.search);
    }
  }, [location.search, storageKey]);

  // 1. Read current pagination and sorting
  const page = useMemo(() => {
    const rawPage = searchParams.get("page");
    return rawPage ? Math.max(1, parseInt(rawPage, 10) || 1) : defaultPage;
  }, [searchParams, defaultPage]);

  const pageSize = useMemo(() => {
    const rawSize = searchParams.get("pageSize");
    return rawSize ? parseInt(rawSize, 10) || defaultPageSize : defaultPageSize;
  }, [searchParams, defaultPageSize]);

  const sortBy = useMemo(() => {
    return searchParams.get("sortBy") || defaultSortBy;
  }, [searchParams, defaultSortBy]);

  const sortOrder = useMemo(() => {
    const rawOrder = searchParams.get("sortOrder");
    return rawOrder === "asc" || rawOrder === "desc" ? rawOrder : defaultSortOrder;
  }, [searchParams, defaultSortOrder]);

  // 2. Read filter values from URL or fallback to defaults
  const filters = useMemo(() => {
    const result = { ...defaultFilters } as TFilters;
    for (const key of Object.keys(defaultFilters)) {
      const val = searchParams.get(key);
      if (val !== null && val !== undefined) {
        // preserve boolean if default is boolean
        if (typeof defaultFilters[key] === "boolean") {
          (result as any)[key] = val === "true";
        } else if (typeof defaultFilters[key] === "number") {
          (result as any)[key] = Number(val);
        } else {
          (result as any)[key] = val;
        }
      }
    }
    return result;
  }, [searchParams, defaultFilters]);

  // 3. Mutators that update URL params without refreshing the page
  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newPage === defaultPage) {
            next.delete("page");
          } else {
            next.set("page", String(newPage));
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, defaultPage]
  );

  const setSorting = useCallback(
    (newSortBy: string, newSortOrder: "asc" | "desc") => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newSortBy === defaultSortBy) {
            next.delete("sortBy");
          } else {
            next.set("sortBy", newSortBy);
          }

          if (newSortOrder === defaultSortOrder) {
            next.delete("sortOrder");
          } else {
            next.set("sortOrder", newSortOrder);
          }
          // Reset page when sorting changes
          next.delete("page");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, defaultSortBy, defaultSortOrder]
  );

  const setFilters = useCallback(
    (patch: Partial<TFilters>, resetToFirstPage = true) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (
              value === undefined ||
              value === null ||
              value === "" ||
              value === (defaultFilters as any)[key]
            ) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          if (resetToFirstPage) {
            next.delete("page");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, defaultFilters]
  );

  const resetFilters = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of Object.keys(defaultFilters)) {
          next.delete(key);
        }
        next.delete("page");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams, defaultFilters, storageKey]);

  // Check if any non-default filters are active
  const hasActiveFilters = useMemo(() => {
    for (const key of Object.keys(defaultFilters)) {
      const val = searchParams.get(key);
      if (val !== null && val !== String((defaultFilters as any)[key])) {
        return true;
      }
    }
    return false;
  }, [searchParams, defaultFilters]);

  return {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    hasActiveFilters,
    setPage,
    setSorting,
    setFilters,
    resetFilters,
  };
}
